/**
 * HeaderComponent.js — Products page sub-header.
 *
 * Shows ONLY page-level controls for the Products view:
 *   - Page title + company subtitle
 *   - Cards / Table toggle
 *   - "Rules Engine Active" status badge
 *
 * NO logo. NO username. NO logout button. Those live in Navigation.jsx.
 */

import { motion } from "framer-motion";
import { FiActivity } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

const HeaderComponent = ({
  darkMode,
  viewMode,
  setViewMode,
  secondaryTextColor,
}) => {
  const { tenant } = useAuth();
  const { t } = useLanguage();
// isRTL
  return (
    <motion.div
      initial={{ y: -10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className={`flex flex-col  items-start sm:items-center sm:flex-row justify-between gap-3 mb-6 `}
    >
      {/* Page title */}
      <div>
        <h2
          className={`text-xl font-bold ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          {t("performance_overview")}
        </h2>
        <p className={`text-sm mt-0.5 ${secondaryTextColor}`}>
          {tenant?.name || t("dashboard_subtitle")}
        </p>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-3">
        {/* Cards / Table toggle */}
        <div
          className={`flex rounded-xl overflow-hidden border ${
            darkMode ? "bg-gray-800 border-gray-700" : "bg-gray-100 border-gray-200"
          }`}
        >
          {["cards", "table"].map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-2 text-sm font-medium transition-all capitalize ${
                viewMode === mode
                  ? "bg-blue-600 text-white"
                  : darkMode
                  ? "text-gray-400 hover:text-white hover:bg-gray-700"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
              }`}
            >
              {mode === "cards" ? t("view_cards") : t("view_table")}
            </button>
          ))}
        </div>

        {/* Rules Engine Active badge */}
        <div
          className={`hidden lg:flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${
            darkMode
              ? "bg-green-500/10 text-green-400 border border-green-500/20"
              : "bg-green-50 text-green-700 border border-green-200"
          }`}
        >
          <FiActivity className="animate-pulse text-xs" />
          <span className="font-medium">{t("rules_engine_active")}</span>
        </div>
      </div>
    </motion.div>
  );
};

export default HeaderComponent;
